/* DACKO week picker: a drop-down calendar for a workshop start date (Casey 2026-10-17). Only
   Mondays from the field's min can be picked, and the chosen Monday to Friday is highlighted,
   because every course runs that week and the browser's own calendar cannot be styled.
   Progressive enhancement over the native <input type="date" min step="7" required> that
   snippets/dacko-course-start-date.liquid prints: the input stays in the form (it posts the value
   and keeps its validation) and is hidden; the field's label moves to a trigger button drawn as
   the field. Without JavaScript the native field shows as before. Picking a Monday highlights its
   week and keeps the calendar open; OK writes YYYY-MM-DD to the input, fires input and change (so
   dacko-course-week.js keeps its line) and closes it. Escape, Tab out or a click elsewhere close
   it without saving the pick. Strings come from
   data attributes on [data-week-picker] (locale keys in the section). Dates are built at noon,
   so no time zone or clock change moves a day. Re-inits on shopify:section:load. */
(() => {
  const lang = document.documentElement.lang || 'en';
  const DAY = 86400000;
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const parse = (s) => {
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s || '') ? new Date(`${s}T12:00:00`) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  };
  const addDays = (d, n) => {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
  };
  const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));
  const monthKey = (d) => iso(d).slice(0, 7);
  const fmt = (d, opts) => d.toLocaleDateString(lang, opts);
  let uid = 0;

  function initPicker(root) {
    if (root.dataset.weekPickerInit) return; // guard against double-binding
    const input = root.querySelector('input[type="date"]');
    const label = root.querySelector('label');
    const caret = root.querySelector('[data-week-picker-caret]');
    if (!input || !label) return;
    root.dataset.weekPickerInit = '1';
    const base = input.id || `dacko-week-picker-${++uid}`;
    const min = parse(input.min) || mondayOf(addDays(new Date(new Date().setHours(12, 0, 0, 0)), 7));
    const s = root.dataset;

    const wrap = document.createElement('div');
    wrap.className = 'dacko-week-picker';
    wrap.innerHTML = `
      <button type="button" class="field__input dacko-week-picker__trigger" id="${base}-trigger" aria-haspopup="dialog" aria-expanded="false" aria-controls="${base}-popup">
        <span class="dacko-week-picker__value" id="${base}-value"></span>
      </button>
      <div class="dacko-week-picker__popup" id="${base}-popup" role="dialog" aria-modal="false" hidden>
        <div class="dacko-week-picker__head">
          <button type="button" class="dacko-week-picker__nav" data-week-picker-prev><span aria-hidden="true">&lsaquo;</span></button>
          <p class="dacko-week-picker__month" aria-live="polite"></p>
          <button type="button" class="dacko-week-picker__nav" data-week-picker-next><span aria-hidden="true">&rsaquo;</span></button>
        </div>
        <table class="dacko-week-picker__grid"><thead><tr></tr></thead><tbody></tbody></table>
        <div class="dacko-week-picker__foot">
          <button type="button" class="button dacko-week-picker__ok" data-week-picker-ok disabled></button>
        </div>
      </div>`;
    input.after(wrap);
    const error = document.createElement('p');
    error.className = 'dacko-week-picker__error';
    error.id = `${base}-error`;
    error.hidden = true;
    error.textContent = s.required || '';
    wrap.after(error);

    const trigger = wrap.querySelector('.dacko-week-picker__trigger');
    const valueText = wrap.querySelector('.dacko-week-picker__value');
    const popup = wrap.querySelector('.dacko-week-picker__popup');
    const monthText = wrap.querySelector('.dacko-week-picker__month');
    const prev = wrap.querySelector('[data-week-picker-prev]');
    const next = wrap.querySelector('[data-week-picker-next]');
    const tbody = wrap.querySelector('tbody');
    const ok = wrap.querySelector('[data-week-picker-ok]');
    ok.textContent = s.confirm || 'OK';
    if (caret) {
      caret.hidden = false;
      trigger.append(caret);
    }
    popup.setAttribute('aria-label', s.dialog || '');
    prev.setAttribute('aria-label', s.previous || '');
    next.setAttribute('aria-label', s.next || '');

    // The label names the trigger, followed by the chosen date or the placeholder
    label.id = label.id || `${base}-label`;
    label.htmlFor = trigger.id;
    trigger.setAttribute('aria-labelledby', `${label.id} ${valueText.id}`);
    const described = [input.getAttribute('aria-describedby'), error.id].filter(Boolean).join(' ');
    trigger.setAttribute('aria-describedby', described);
    input.hidden = true;
    input.tabIndex = -1;

    // Weekday header, Monday first, from the page language
    const headRow = wrap.querySelector('thead tr');
    for (let i = 0; i < 7; i++) {
      const d = addDays(new Date('2026-01-05T12:00:00'), i); // a Monday
      const th = document.createElement('th');
      th.scope = 'col';
      th.abbr = fmt(d, { weekday: 'long' });
      th.textContent = fmt(d, { weekday: 'short' });
      headRow.append(th);
    }

    let view = null; // first of the shown month
    let focusDate = null;
    let pending = ''; // the Monday picked in the open calendar, saved to the field only by OK

    const chosen = () => {
      const d = parse(input.value);
      return d && d.getDay() === 1 && d >= min ? d : null;
    };
    const showValue = () => {
      const d = chosen();
      valueText.textContent = d ? fmt(d, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : s.placeholder || '';
      trigger.classList.toggle('is-empty', !d);
    };
    const firstAvailable = (month) => {
      let d = mondayOf(month);
      while (d < min || d.getMonth() !== month.getMonth()) d = addDays(d, 7);
      return d.getMonth() === month.getMonth() ? d : null;
    };

    function render(month) {
      view = new Date(month.getFullYear(), month.getMonth(), 1, 12);
      popup.dataset.month = monthKey(view);
      monthText.textContent = fmt(view, { month: 'long', year: 'numeric' });
      prev.disabled = monthKey(view) <= monthKey(min);
      next.disabled = false;
      const pick = parse(pending);
      ok.disabled = !pick;
      const weekStart = pick ? iso(pick) : '';
      const weekEnd = pick ? iso(addDays(pick, 4)) : '';
      tbody.textContent = '';
      for (let row = mondayOf(view); row.getMonth() === view.getMonth() || row < view; row = addDays(row, 7)) {
        const tr = document.createElement('tr');
        for (let i = 0; i < 7; i++) {
          const d = addDays(row, i);
          const key = iso(d);
          const td = document.createElement('td');
          td.dataset.date = key;
          if (d.getMonth() !== view.getMonth()) td.classList.add('is-outside');
          if (pick && key >= weekStart && key <= weekEnd) td.classList.add('is-in-week');
          if (i === 0 && d.getMonth() === view.getMonth() && d >= min) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'dacko-week-picker__day';
            b.dataset.date = key;
            b.textContent = d.getDate();
            b.setAttribute('aria-label', fmt(d, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }));
            b.setAttribute('aria-pressed', key === weekStart ? 'true' : 'false');
            b.tabIndex = -1;
            td.append(b);
          } else {
            td.textContent = d.getDate();
          }
          tr.append(td);
        }
        tbody.append(tr);
      }
      const target = focusDate && monthKey(focusDate) === monthKey(view) ? focusDate : (pick && monthKey(pick) === monthKey(view) ? pick : firstAvailable(view));
      const btn = target && tbody.querySelector(`button[data-date="${iso(target)}"]`);
      if (btn) btn.tabIndex = 0;
      return btn;
    }

    function focusDay(d) {
      if (!d || d < min) return;
      focusDate = d;
      const btn = monthKey(d) === monthKey(view) ? tbody.querySelector(`button[data-date="${iso(d)}"]`) : null;
      if (btn) {
        tbody.querySelectorAll('button').forEach((b) => (b.tabIndex = b === btn ? 0 : -1));
        btn.focus();
      } else {
        render(d)?.focus();
      }
    }

    function open() {
      if (!popup.hidden) return;
      focusDate = null;
      pending = chosen() ? iso(chosen()) : '';
      const btn = render(chosen() || min);
      popup.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      (btn || next).focus();
    }

    function close(returnFocus) {
      if (popup.hidden) return;
      popup.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus();
    }

    function choose(key) {
      input.value = key;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      error.hidden = true;
      trigger.removeAttribute('aria-invalid');
      showValue();
      close(true);
    }

    trigger.addEventListener('click', () => (popup.hidden ? open() : close(false)));
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' && popup.hidden) {
        e.preventDefault();
        open();
      }
    });
    prev.addEventListener('click', () => {
      focusDate = null;
      render(new Date(view.getFullYear(), view.getMonth() - 1, 1, 12));
    });
    next.addEventListener('click', () => {
      focusDate = null;
      render(new Date(view.getFullYear(), view.getMonth() + 1, 1, 12));
    });
    // A pick highlights its week and keeps the calendar open, so the shopper sees the course
    // week before confirming it with OK (Casey 2026-10-17)
    tbody.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-date]');
      if (!b) return;
      pending = b.dataset.date;
      focusDate = parse(pending);
      render(view)?.focus();
    });
    ok.addEventListener('click', () => {
      if (pending) choose(pending);
    });
    popup.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
        return;
      }
      const day = e.target.closest('button[data-date]');
      if (!day) return;
      const d = parse(day.dataset.date);
      const moves = {
        ArrowDown: () => addDays(d, 7),
        ArrowRight: () => addDays(d, 7),
        ArrowUp: () => addDays(d, -7),
        ArrowLeft: () => addDays(d, -7),
        PageDown: () => firstAvailable(new Date(d.getFullYear(), d.getMonth() + 1, 1, 12)),
        PageUp: () => firstAvailable(new Date(d.getFullYear(), d.getMonth() - 1, 1, 12)),
        Home: () => firstAvailable(view),
        End: () => {
          const b = [...tbody.querySelectorAll('button[data-date]')].pop();
          return b && parse(b.dataset.date);
        },
      };
      if (!moves[e.key]) return;
      e.preventDefault();
      focusDay(moves[e.key]());
    });
    // Tab out of the picker, or a click anywhere else, closes it. Checked a tick later, because
    // drawing another month removes the focused day before the new one takes focus.
    wrap.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!wrap.contains(document.activeElement)) close(false);
      });
    });
    document.addEventListener('pointerdown', (e) => {
      if (!wrap.contains(e.target)) close(false);
    });

    // An empty field on send: the browser cannot point its bubble at the hidden input, so the
    // picker shows its own message, and takes focus when it is the form's first problem.
    input.addEventListener('invalid', (e) => {
      e.preventDefault();
      error.hidden = false;
      trigger.setAttribute('aria-invalid', 'true');
      const first = input.form && [...input.form.elements].find((el) => el.willValidate && !el.validity.valid);
      if (first === input) setTimeout(() => trigger.focus());
    });

    showValue();
  }

  const initAll = (scope) => (scope || document).querySelectorAll('[data-week-picker]').forEach(initPicker);

  initAll(); // `defer` guarantees the DOM is parsed
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
